import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconAvatarComponent, ImageAvatarComponent } from '@improver/components/avatar';
import { ContextMenuDirective, ContextMenuItemComponent } from '@improver/components/context-menu';
import { ModalsService, ModalWidth } from '@improver/components/modals';
import {
  AuthDataLayerModule,
  AuthNavigationService,
  AuthorizedUser,
  AuthService,
  LoggedUserService,
} from '@improver/infrastructure/auth-data-layer';
import { BaseNavigationService, PageTitleService, RouteHistoryService } from '@improver/infrastructure/navigation-data-layer';
import { PeopleNavigationService } from '@improver/people/people-data-layer/navigation';
import { V4ComapanyEmployee } from '@improver/people/people-data-layer/v4-company';
import { MobileDirective } from '@improver/shared-ui/responsive';
import { PersonnelPermissions } from '@improver/shared/permissions';
import { NavButtonComponent } from '@improver/shared/ui/components/nav-button';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { filter, take, tap, withLatestFrom } from 'rxjs/operators';
import { V4CompanyModalComponent } from './v4-company-modal/v4-company-modal.component';

const isEmployment = (user: AuthorizedUser): boolean => !!(user.id && user.companyId && user.employmentId);
const isUserOnly = (user: AuthorizedUser): boolean => !!(user.id && !user.employmentId);

@Component({
  selector: 'app-profile-dropdown',
  templateUrl: './profile-dropdown.component.html',
  styleUrls: ['./profile-dropdown.component.scss'],
  imports: [
    AsyncPipe,
    AuthDataLayerModule,
    ContextMenuDirective,
    ContextMenuItemComponent,
    IconAvatarComponent,
    ImageAvatarComponent,
    MobileDirective,
    NavButtonComponent,
    TranslateModule,
  ],
})
export class ProfileDropdownComponent implements OnInit {
  private readonly peopleNavigationService: PeopleNavigationService = inject(PeopleNavigationService);
  private readonly authNavigationService: AuthNavigationService = inject(AuthNavigationService);
  private readonly baseNavigationService: BaseNavigationService = inject(BaseNavigationService);
  private readonly loggedUserService: LoggedUserService = inject(LoggedUserService);
  private readonly routeHistoryService: RouteHistoryService = inject(RouteHistoryService);
  private readonly modalsService: ModalsService = inject(ModalsService);
  private readonly pageTitleService: PageTitleService = inject(PageTitleService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  @ViewChild(ContextMenuDirective) contextMenuDirective: ContextMenuDirective;
  @ViewChild('profileDropdownAvatar') profileDropdownAvatar: ElementRef<HTMLElement>;
  @Input() companyEmployees: V4ComapanyEmployee[];
  @Input() profileMenu: boolean;
  user: AuthorizedUser;
  readonly userImageUrl$: Observable<string> = this.loggedUserService.getUserPhoto();
  readonly loggedAsSupportUser$: Observable<boolean> = this.loggedUserService.isLoggedAsSupportUser();
  readonly MANAGE_INST_PERSONNEL_VIEW_BASIC = PersonnelPermissions.MANAGE_INST_PERSONNEL_VIEW_BASIC;

  ngOnInit() {
    this.updateUserData();
  }

  menuClosedByEscape() {
    setTimeout(() => this.profileDropdownAvatar.nativeElement.focus());
  }

  openUserMenu(event: Event) {
    this.contextMenuDirective.open(event);
  }

  logout() {
    this.baseNavigationService.goToLogout();
  }

  logoutToMM() {
    const { mmCallbackUrl } = this.loggedUserService.getAuthorizedUserDataValue();

    this.authService.logoutWithoutRedirect().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.baseNavigationService.navigateToExternalUrl(mmCallbackUrl);
  }

  goToProfilePage() {
    this.authNavigationService.goToProfilePage();
  }

  goToEmployeePage() {
    this.pageTitleService
      .getCurrentPageTitle()
      .pipe(
        tap((title) => this.routeHistoryService.updateCurrentRoute({ backVisibility: true, title })),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (isEmployment(this.user)) {
          this.navigateToEmployment();

          return;
        }

        if (isUserOnly(this.user)) {
          this.navigateToUserOnly();

          return;
        }

        this.navigateToVacant();
      });
  }

  goToHelpCenter() {
    this.baseNavigationService.goToHelpCenter();
  }

  openV4CompanyModal() {
    this.modalsService.open(V4CompanyModalComponent, {}, ModalWidth.medium);
  }

  private updateUserData() {
    this.isLoggedIn()
      .pipe(
        filter((isLoggedIn) => isLoggedIn),
        withLatestFrom(this.loggedUserService.getAuthorizedUserData()),
        tap(([, userData]) => {
          this.user = userData ? userData : null;
        })
      )
      .subscribe();
  }

  private isLoggedIn(): Observable<boolean> {
    return this.loggedUserService.isLoggedIn.pipe(takeUntilDestroyed(this.destroyRef));
  }

  private navigateToEmployment() {
    this.peopleNavigationService.goToEmployeeProfile({
      userId: this.user.id,
      employmentId: this.user.employmentId,
      companyId: this.user.companyId,
      section: 'employment',
    });
  }

  private navigateToUserOnly() {
    this.peopleNavigationService.goToPersonnelUser(this.user.id);
  }

  private navigateToVacant() {
    this.peopleNavigationService.goToVacantProfile({
      employmentId: this.user.employmentId,
      companyId: this.user.companyId,
      section: 'employment',
    });
  }
}
